import { Button, TextField, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useCreateChannelGuide } from '~/api/channel-guides';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent, DialogForm } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { usePaginationParams } from '~/hooks/pagination';

export interface NewChannelGuideDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  open: boolean;
}

export const NewChannelGuideDialog = ({ close, open }: NewChannelGuideDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const link = useLink();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() || '';
  const { page, perPage } = usePaginationParams();

  const [createChannelGuide] = useCreateChannelGuide();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
    },
    onSubmit: async (input) => {
      const { data } = await createChannelGuide({
        variables: { input: { ...input, networkId: currentNetwork.id }, page, perPage, search },
      });
      navigate(link(`/channel-guides/${data?.createChannelGuide?.channelGuide.id}`));
    },
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      name: string().required(t('errors.validation.required')).label(t('common.labels.name')),
    }),
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={close}
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <DialogTitle onClose={close}>{t('pages.channel_guides.dialogs.new.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.channel_guides.dialogs.new.description')}</DialogContentTitle>

        <DialogForm onSubmit={formik.handleSubmit} id="new-channel-guide-form">
          <label>
            <span className={`label required`}>{t('common.labels.name')}</span>
            <TextField
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={(formik.touched.name && formik.errors.name) || ' '}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
              focused
              autoFocus
            />
          </label>
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          form="new-channel-guide-form"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
